"use client";

import { useEffect, useState } from "react";

type Sonner = typeof import("sonner");

/**
 * `SITE-SPD04`: sonner's toaster (9 KB on the wire) loads after the page has
 * loaded and the browser is idle, instead of in the script every page waits
 * for; most pages never show a toast. A toast fired before then is not lost:
 * sonner keeps every toast it was asked for, and once its toaster has mounted
 * (a child's effect runs before this one) each one still active is fired again
 * under its own id, which shows it.
 */
export function LazyToaster() {
  const [sonner, setSonner] = useState<Sonner | null>(null);

  useEffect(() => {
    let cancelled = false;
    let idle: number | null = null;
    const load = () => {
      void import("sonner").then((module) => {
        if (!cancelled) setSonner(module);
      });
    };
    const whenIdle = () => {
      if ("requestIdleCallback" in window) idle = window.requestIdleCallback(load, { timeout: 3000 });
      else load();
    };
    if (document.readyState === "complete") whenIdle();
    else window.addEventListener("load", whenIdle, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", whenIdle);
      if (idle !== null) window.cancelIdleCallback(idle);
    };
  }, []);

  useEffect(() => {
    if (!sonner) return;
    for (const early of sonner.toast.getToasts()) {
      if (!("dismiss" in early)) sonner.toast(early.title, early);
    }
  }, [sonner]);

  if (!sonner) return null;
  const { Toaster } = sonner;
  return <Toaster position="bottom-right" richColors />;
}
